import {
  PlusCircle,
  Pencil,
  XCircle,
  CheckCircle2,
  RefreshCw,
  StickyNote,
  FileUp,
  FileX,
  CalendarPlus,
  DollarSign,
  Circle,
  type LucideIcon,
} from 'lucide-react'

// Estratto da 3 copie duplicate identiche (CustomerDetails/shared.tsx, AssignmentDetails/
// shared.tsx, PropertyDetails/shared.tsx): se una veniva aggiornata e le altre no, un evento
// con un tipo presente in una sola delle copie mostrava il valore grezzo invece della label
// tradotta nelle altre due pagine. Copre sia TimelineEventType (cronologia entità) sia
// PlannerEvent.EventType (agenda) - quest'ultimo aggiunto solo nella copia di PropertyDetails.
// NOTA: TimelineGlobalPage.tsx ha una mappa strutturalmente diversa (chiavi icona-nome stile
// Bootstrap/FontAwesome inviate dal backend, non TimelineEventType) e resta volutamente fuori
// da questo consolidamento - è un dominio diverso, non una quarta copia della stessa mappa.
export const TIMELINE_EVENT_TYPE_LABELS: Record<string, string> = {
  Created: 'Creato',
  Modified: 'Modificato',
  Deleted: 'Eliminato',
  Viewed: 'Visualizzato',
  Shared: 'Condiviso',
  EmailSent: 'Email inviata',
  PhoneCalled: 'Chiamata effettuata',
  PhoneCall: 'Chiamata effettuata',
  MessageSent: 'Messaggio inviato',
  VisitScheduled: 'Visita programmata',
  VisitCompleted: 'Visita completata',
  OfferReceived: 'Offerta ricevuta',
  OfferAccepted: 'Offerta accettata',
  OfferRejected: 'Offerta rifiutata',
  StatusChanged: 'Stato modificato',
  DocumentUploaded: 'Documento caricato',
  PublishedToPortal: 'Pubblicato su portale',
  UnpublishedFromPortal: 'Rimosso da portale',
  ConvertedToNews: 'Convertito in notizia',
  PriceChanged: 'Prezzo modificato',
  PhotoUploaded: 'Foto caricata',
  NoteAdded: 'Nota aggiunta',
  AssignmentCreated: 'Incarico creato',
  AssignmentClosed: 'Incarico chiuso',
  CustomerContacted: 'Cliente contattato',
  AppointmentScheduled: 'Appuntamento programmato',
  AppointmentCompleted: 'Appuntamento completato',
  // PlannerEvent.EventType
  Appointment: 'Appuntamento',
  Visit: 'Visita',
  Meeting: 'Riunione',
  Call: 'Chiamata',
  Other: 'Altro',
}

export function getTimelineEventTypeLabel(eventType: string): string {
  return TIMELINE_EVENT_TYPE_LABELS[eventType] ?? eventType
}

const PROPERTY_STATUS_LABELS: Record<string, string> = {
  Draft: 'Bozza',
  Active: 'Attiva',
  UnderNegotiation: 'In trattativa',
  Sold: 'Venduta',
  Rented: 'Affittata',
  Withdrawn: 'Ritirata',
  Suspended: 'Sospesa',
}

/** Traduce le descrizioni degli eventi storici già salvati con valori enum inglesi. */
export function getTimelineEventDescription(description?: string): string | undefined {
  if (!description) return description
  const statusChange = description.match(/^Stato cambiato da (.+) a (.+)$/)
  if (!statusChange) return description

  const [, previousStatus, nextStatus] = statusChange
  return `Stato cambiato da ${PROPERTY_STATUS_LABELS[previousStatus] ?? previousStatus} a ${PROPERTY_STATUS_LABELS[nextStatus] ?? nextStatus}`
}

// Prima solo in AssignmentDetails/shared.tsx: CustomerDetails e PropertyDetails
// renderizzavano la timeline senza icone per-tipo.
export const TIMELINE_ICON_MAP: Record<string, LucideIcon> = {
  Created: PlusCircle,
  Modified: Pencil,
  Updated: Pencil,
  Deleted: XCircle,
  Closed: XCircle,
  Sold: CheckCircle2,
  Renewed: RefreshCw,
  StatusChanged: RefreshCw,
  NoteAdded: StickyNote,
  DocumentAdded: FileUp,
  DocumentUploaded: FileUp,
  PhotoUploaded: FileUp,
  DocumentDeleted: FileX,
  VisitScheduled: CalendarPlus,
  AppointmentScheduled: CalendarPlus,
  VisitCompleted: CheckCircle2,
  AppointmentCompleted: CheckCircle2,
  OfferReceived: DollarSign,
  OfferAccepted: CheckCircle2,
  OfferRejected: XCircle,
  PriceChanged: DollarSign,
  AssignmentCreated: PlusCircle,
  AssignmentClosed: XCircle,
}

export function getTimelineEventIcon(eventType: string): LucideIcon {
  return TIMELINE_ICON_MAP[eventType] ?? Circle
}

const TIMELINE_COLOR_MAP: Record<string, string> = {
  success: 'var(--color-success-500)',
  danger: 'var(--color-danger-500)',
  warning: 'var(--color-warning-500)',
  info: 'var(--color-info-500)',
  primary: 'var(--color-primary-500)',
  secondary: 'var(--color-secondary-500)',
}

export function getTimelineEventColor(color?: string): string {
  return TIMELINE_COLOR_MAP[color ?? ''] ?? 'var(--color-primary-500)'
}
